import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { PantallasCustomizablesService, IOpcionVariable, IPantallaCustomizableSaveRequest, ICamposTablaRequest, INuevoCampoSaveRequest } from '../services/pantallas-customizables.service';
import { IPantallaCustomizable } from '../interfaces/pantalla-customizable.interface';
import { SessionService } from '@shared/helpers/sesion.service';
import { MatDialog } from '@angular/material/dialog';
import { AbcEmpresasCustomizablesComponent } from '@p-parametria/mantenimiento/abc-empresas/components/abc-empresas-customizables/abc-empresas-customizables.component';
import Swal from 'sweetalert2';

interface ICampoCustomizable {
  orden: number;
  campo: string;
  label: string;
}

@Component({
  selector: 'app-pantalla-customizable-form',
  templateUrl: './pantalla-customizable-form.component.html',
  providers: [MessageService]
})
export class PantallaCustomizableFormComponent implements OnInit, OnChanges {
  @Input() model: IPantallaCustomizable | null = null;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string }>();

  form: FormGroup;
  loading: boolean = false;
  opcionesTipo: IOpcionVariable[] = [];
  isEditMode: boolean = false;
  camposCustomizables: ICampoCustomizable[] = [];
  displayNuevoCampoDialog: boolean = false;
  campoParaEditar: ICampoCustomizable | null = null;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private pantallasCustomizablesService: PantallasCustomizablesService,
    private sessionService: SessionService,
    private dialog: MatDialog
  ) {
    this.form = this.fb.group({
      clave: ['', [Validators.required, Validators.maxLength(4)]],
      descripcion: ['', [Validators.required]],
      tipo: ['', [Validators.required]]
    });

    // Convertir a mayúsculas automáticamente
    this.form.get('clave')?.valueChanges.subscribe(value => {
      if (value) {
        const upperValue = value.toUpperCase();
        if (upperValue !== value) {
          this.form.get('clave')?.setValue(upperValue, { emitEvent: false });
        }
      }
    });
  }

  ngOnInit(): void {
    this.loadOpcionesTipo();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model'] && changes['model'].currentValue) {
      this.isEditMode = true;
      this.initializeForm();
      this.loadCamposCustomizables();
    } else {
      this.isEditMode = false;
      this.form.reset();
      this.camposCustomizables = [];
    }
  }

  initializeForm(): void {
    if (this.model) {
      this.form.patchValue({
        clave: this.model.clave,
        descripcion: this.model.descripcion,
        tipo: this.model.tipo
      });
      
      // En modo edición, todos los campos están deshabilitados
      if (this.isEditMode && this.model) {
        this.form.get('clave')?.disable();
        this.form.get('descripcion')?.disable();
        this.form.get('tipo')?.disable();
      }
    }
  }

  loadOpcionesTipo(): void {
    this.pantallasCustomizablesService.getOpcionesTipo().subscribe({
      next: (response) => {
        if (response.success) {
          this.opcionesTipo = response.data;
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar las opciones de tipo'
          });
        }
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar las opciones de tipo'
        });
        console.error('Error:', error);
      }
    });
  }

  loadCamposCustomizables(): void {
    if (!this.model?.clave) return;

    const request: ICamposTablaRequest = {
      sClave: this.model.clave,
      iOrden: 0,
      sCampo: "",
      sEtiqueta: "",
      iObliga: 0
    };

    this.pantallasCustomizablesService.getCamposTabla(request).subscribe({
      next: (response) => {
        // Cargar datos si existen, independientemente del flag success
        if (response.data && response.data.length > 0) {
          this.camposCustomizables = response.data.map((item, index) => ({
            orden: parseInt(item.pagina) || index + 1,
            campo: item.campo?.trim() || '',
            label: item.etiqueta?.trim() || ''
          }));
        } else {
          this.camposCustomizables = [];
        }
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar los campos customizables'
        });
        console.error('Error:', error);
      }
    });
  }

  openCustomizables(): void {
    this.dialog.open(AbcEmpresasCustomizablesComponent, {
      width: '1000px',
      height: '1000px',
      disableClose: true,
      panelClass: ['custom-dialog-zindex'],
      hasBackdrop: true,
      autoFocus: false,
      data: {
        sTabla: 'pantvar',
        sClave: `${this.form.get('clave')?.value}`,
        sNombre: this.form.get('descripcion')?.value,
        sTipo: 'Opcion'
      }
    });
  }

  openNuevo(): void {
    this.campoParaEditar = null; // Asegurar que no hay campo para editar
    this.displayNuevoCampoDialog = true;
  }

  onNuevoCampoDialogClosed(): void {
    this.displayNuevoCampoDialog = false;
  }

  onNuevoCampoSaved(result: { success: boolean; message: string; data?: any }): void {
    if (result.success) {
      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: result.message
      });
      
      // Recargar la tabla después de agregar un nuevo campo
      this.loadCamposCustomizables();
    }
    this.displayNuevoCampoDialog = false;
  }

  editarCampo(campo: ICampoCustomizable): void {
    this.campoParaEditar = campo;
    this.displayNuevoCampoDialog = true;
  }

  eliminarCampo(campo: ICampoCustomizable): void {
    // Confirmar antes de eliminar usando SweetAlert2
    Swal.fire({
      title: '¿Está seguro?',
      text: `¿Está seguro que desea eliminar el campo "${campo.campo}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      iconColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.loading = true;
        
        const request: INuevoCampoSaveRequest = {
          iAccion: 3, // Eliminar
          sClave: this.model?.clave || '',
          iOrden: campo.orden,
          sCampo: campo.campo,
          sEtiqueta: campo.label,
          iObliga: 0
        };

        this.pantallasCustomizablesService.saveNuevoCampo(request).subscribe({
          next: (response) => {
            const errorCode = response.data?.[0]?.error;
            if (errorCode === "0") {
              this.messageService.add({
                severity: 'success',
                summary: 'Éxito',
                detail: 'Campo eliminado correctamente'
              });
              
              // Recargar la tabla después de eliminar
              this.loadCamposCustomizables();
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: errorCode || 'Error al eliminar el campo'
              });
            }
          },
          error: (error) => {
            console.error('Error:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al eliminar el campo'
            });
          },
          complete: () => {
            this.loading = false;
          }
        });
      }
    });
  }

  create(): void {
    if (this.form.valid) {
      this.loading = true;
      
      const formData = this.form.value;
      const request: IPantallaCustomizableSaveRequest = {
        sUsuario: this.sessionService.getUserSession(),
        iAccion: 1, // Crear
        sClave: formData.clave,
        sDescrip: formData.descripcion,
        sTipoPant: formData.tipo,
        iNumReg: 0
      };

      this.pantallasCustomizablesService.savePantallaCustomizable(request).subscribe({
        next: (response) => {
          const errorCode = response.data?.[0]?.error;
          if (errorCode === "0") {
            this.onSave.emit({
              success: true,
              message: 'Pantalla customizable creada correctamente'
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: errorCode || 'Error al crear la pantalla customizable'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al crear la pantalla customizable'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    }
  }

  update(): void {
    if (this.form.valid) {
      this.loading = true;
      
      // Habilitar los campos temporalmente para obtener sus valores
      const claveControl = this.form.get('clave');
      const descripcionControl = this.form.get('descripcion');
      const tipoControl = this.form.get('tipo');
      
      if (claveControl?.disabled) {
        claveControl.enable();
      }
      if (descripcionControl?.disabled) {
        descripcionControl.enable();
      }
      if (tipoControl?.disabled) {
        tipoControl.enable();
      }

      const formData = this.form.value;
      const request: IPantallaCustomizableSaveRequest = {
        sUsuario: this.sessionService.getUserSession(),
        iAccion: 2, // Actualizar
        sClave: formData.clave,
        sDescrip: formData.descripcion,
        sTipoPant: formData.tipo,
        iNumReg: 0
      };

      this.pantallasCustomizablesService.savePantallaCustomizable(request).subscribe({
        next: (response) => {
          const errorCode = response.data?.[0]?.error;
          if (errorCode === "0") {
            this.onSave.emit({
              success: true,
              message: 'Pantalla customizable actualizada correctamente'
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: errorCode || 'Error al actualizar la pantalla customizable'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al actualizar la pantalla customizable'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    }
  }

  onSubmit(): void {
    if (this.model) {
      this.update();
    } else {
      this.create();
    }
  }

  onCancel(): void {
    this.onSave.emit({ success: false, message: '' });
  }

  private markFormGroupTouched(): void {
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.form.get(fieldName);
    if (field && field.errors) {
      if (field.errors['required']) {
        return 'Este campo es requerido';
      }
    }
    return '';
  }
} 